import { fetchEventSource } from '@microsoft/fetch-event-source'
import Debug from 'debug'
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { AuthContext } from '../Auth/AuthProvider'

const log = Debug('ela:EventStreamProvider')
log.log = console.info.bind(console) // eslint-disable-line no-console

class RetriableError extends Error {}

export type EventStreamEvent = {
  event: string
  data?: {
    event?: string
    userId?: string
  }
}
export type Subscriber = {
  onMessage?: (evtdata: EventStreamEvent['data']) => void
  onError?: (err: Error) => void
  // Keep the stream open even when the tab is hidden. Default is to close the
  // stream based on the PageVisibility API.
  keepAliveInBackground?: boolean
}
type SubscribersArray = Array<Subscriber>
export type TContext = {
  subscribe: (subscriber: Subscriber) => void
  unsubscribe: (subscriber: Subscriber) => void
}
// Create a new context
export const EventStreamContext = createContext<TContext>({ subscribe: () => {}, unsubscribe: () => {} })

// Event stream provider component
export const EventStreamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [keepAliveInBackground, setKeepAliveInBackground] = React.useState(false)
  const subscribers = useRef([] as SubscribersArray)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const subscribe = useCallback(
    (subscriber: Subscriber) => {
      subscribers.current.push(subscriber)
      setKeepAliveInBackground(subscribers.current.some((s) => s.keepAliveInBackground))
    },
    [subscribers],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const unsubscribe = useCallback(
    (subscriber: Subscriber) => {
      subscribers.current = subscribers.current.filter((s) => s !== subscriber)
      setKeepAliveInBackground(subscribers.current.some((s) => s.keepAliveInBackground))
    },
    [subscribers],
  )

  useEffect(() => {
    log('keepAliveInBackground', keepAliveInBackground)
  }, [keepAliveInBackground])

  const authContext = useContext(AuthContext)
  if (!authContext) {
    throw new Error('EventStreamProvider must be used within the AuthProvider')
  }
  const { token } = authContext
  useEffect(() => {
    if (token) {
      const ctrl = new AbortController()
      async function setupEventStream() {
        log('Setting up event stream')
        await fetchEventSource('/api/events', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          keepalive: true,
          // Keeps the stream open even when the tab is hidden. Default is to
          // close the stream based on the PageVisibility API.
          openWhenHidden: keepAliveInBackground,
          onmessage(e) {
            // log('Stream event', e)
            const data: EventStreamEvent['data'] = (() => {
              if (typeof e.data === 'string' && e.data.length > 0) {
                return JSON.parse(e.data)
              }
              return e.data
            })()
            log('Stream event data:', data)
            for (const subscriber of subscribers.current) {
              if (subscriber.onMessage) {
                subscriber.onMessage(data)
              }
            }
          },
          onerror(err) {
            console.error('Error listening to event stream. Retrying in 5 seconds.', err)
            for (const subscriber of subscribers.current) {
              if (subscriber.onError) {
                subscriber.onError(err)
              }
            }
            return 5000
            // if(err instanceof FatalError) {
            //   console.error('Fatal: Error listening to event stream', err)
            //   for(const subscriber of subscribers.current) {
            //     if(subscriber.onError) {
            //       subscriber.onError(err)
            //     }
            //   }
            //   throw err
            // } else {
            //   console.error('Error listening to event stream. Retrying in 5 seconds')
            //   // retry in 5 seconds
            //   return 5000
            // }
          },
          onclose() {
            log('Event stream closed')
            throw new RetriableError()
          },
          async onopen() {
            log('Listening to event stream')
            for (const subscriber of subscribers.current) {
              if (subscriber.onMessage) {
                subscriber.onMessage({ event: 'open' })
              }
            }
          },
          signal: ctrl.signal,
        })
      }
      setupEventStream()
      return () => {
        ctrl.abort()
      }
    }
  }, [keepAliveInBackground, token])

  return <EventStreamContext.Provider value={{ subscribe, unsubscribe }}>{children}</EventStreamContext.Provider>
}
